import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rgba, shade } from 'polished';
import React, { forwardRef } from 'react';
import styled, { DefaultTheme, ThemeColors, useTheme } from 'styled-components';
import Flex from './Flex';
import Spinner from './Spinner';

interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    color?: keyof ThemeColors;
    bgColor?: keyof ThemeColors | 'rainbow';
    square?: boolean;
    shadow?: boolean;
    disableFont?: boolean;
    fontSize?: string;
    flexJustify?: string;
    half?: 'left' | 'right';
    dark?: number;
    target?: string;
}

export type IconButtonProps = IButtonProps & {
    icon: IconDefinition;
    iconContent?: React.ReactNode;
    iconRight?: boolean;
    iconSize?: SizeProp;
    small?: boolean;
    large?: boolean;
    href?: string;
    pending?: boolean;
    ref?: React.RefObject<any>;
};

type Size = 'sm' | 'md' | 'lg';

const sizes: Record<Size, number> = {
    sm: 26,
    md: 34,
    lg: 40,
};

type ButtonProps = IButtonProps & {
    size: Size;
    hasText?: boolean;
};

const Button = styled.button<ButtonProps>`
  display: flex;
  flex-shrink: 0;
  justify-content: ${({ flexJustify }) => (flexJustify != null ? flexJustify : 'center')};
  align-items: center;
  ${({ size, hasText }) => hasText !== true && `width: ${sizes[size]}px;`}
  height: ${({ size }) => sizes[size]}px;
  border-radius: ${({ square, size }) => (square ? 0 : sizes[size] / 2)}px;
  ${({ half }) => `
      ${
          half === 'left'
              ? `
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      `
              : ''
      }
      ${
          half === 'right'
              ? `
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      `
              : ''
      }
  `};
  background: ${({ bgColor, theme, disabled, dark }) => getBg(theme, disabled, dark, bgColor)};
  ${({ bgColor }) =>
      bgColor === 'rainbow' &&
      `
    background-size: 300% 300%;
    background-position: 0% 00%;
    animation: gradient 16s linear infinite;
  `}

  ${({ theme, shadow }) => (shadow ? theme.shadows.soft : '')};
  text-align: center;
  border: 0;
  font-size: ${({ hasText, theme }) => (hasText ? theme.typography.fontSize : '16px')};
  padding: ${({ hasText }) => (hasText ? '0 1em' : 0)};
  white-space: nowrap;
  color: ${({ theme, color, disabled }) =>
      disabled ? theme.colors.textDisabled : color ? theme.colors[color] : theme.colors.text};

  ${({ disabled, bgColor, theme, dark }) =>
      !disabled
          ? `
        &:hover {
          ${bgColor !== 'rainbow' && `background-color: ${getBg(theme, disabled, dark ? dark + 0.03 : 0.03, bgColor)};`}

        }
      `
          : ''}

  @keyframes gradient {
    0% {
      background-position: 0% 0%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 00%;
    }
  }
  transition: background-color 0.2s linear;

  &:focus-within {
    outline: 0;
  }

  &:focus-visible {
    outline: 1px solid black;
  }
`;

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            icon,
            color,
            bgColor,
            square,
            shadow,
            disabled,
            iconRight,
            disableFont,
            fontSize,
            iconContent,
            flexJustify,
            children,
            iconSize,
            pending,
            small,
            large,
            ...rest
        },
        ref,
    ) => {
        const theme = useTheme();

        const textEl = children ? (
            <Text
                fontSize={fontSize}
                className={`${!disableFont ? 'f1-500' : ''} mr-2`}
                large={large}
                noPadding={iconRight}
            >
                {children}
            </Text>
        ) : (
            <></>
        );

        const iconEl = iconContent ? (
            iconContent
        ) : (
            <FontAwesomeIcon
                size={iconSize ?? '1x'}
                icon={icon}
                color={disabled ? theme.colors.textDisabled : color ? theme.colors[color] : theme.colors.text}
            />
        );

        return (
            <Button
                as={rest.href ? 'a' : 'button'}
                color={color}
                bgColor={bgColor}
                square={square}
                hasText={!!children}
                shadow={shadow}
                disabled={disabled}
                flexJustify={flexJustify}
                size={large ? 'lg' : small ? 'sm' : 'md'}
                ref={ref}
                {...rest}
            >
                <Flex align="center" $reverse={iconRight}>
                    {pending ? <Spinner /> : iconEl}
                    {textEl}
                </Flex>
            </Button>
        );
    },
);

export default IconButton;

type TextProps = {
    large?: boolean;
    fontSize?: string;
    noPadding?: boolean;
};
const Text = styled.span<TextProps>`
    padding-left: ${({ large }) => (large ? 12 : 10)}px;
    font-size: ${({ fontSize }) => (fontSize ? fontSize : '15px')};
    ${({ noPadding }) => (noPadding ? 'padding-left:0;px;' : '')}
`;

function getBg(
    theme: DefaultTheme,
    disabled?: boolean,
    dark?: number,
    bgColor?: keyof ThemeColors | 'rainbow',
): string {
    if (disabled) return theme.colors.disabled;

    if (bgColor === 'rainbow') return 'linear-gradient(in hsl longer hue 45deg, #fb9f9f 0 0)';

    const col = bgColor ? theme.colors[bgColor] : theme.colors.fill;

    if (dark) {
        if (bgColor === 'transparent') return rgba(0, 0, 0, 0.03);

        return shade(dark, col);
    }

    return col;
}
